import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { UserFormData } from '../../../../shared/models/user-form-data';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Role } from '../../../../shared/models/role';
import { AdminService } from '../../services/admin';
import { map, Observable, startWith } from 'rxjs';

function passwordComplexityValidator(control: AbstractControl): ValidationErrors | null {
  	const value = control.value;

	if(!value) {
		return null;
	}

  	const errors: { [key: string]: any } = {};

  	const minLength = 8;

	if(value.length < minLength) {
		errors['minLength'] = { requiredLength: minLength, actualLength: value.length };
	}

	if(!/[a-z]/.test(value)) {
		errors['hasLowercase'] = true;
	}

	if(!/[A-Z]/.test(value)) {
		errors['hasUppercase'] = true;
	}

	if(!/[0-9]/.test(value)) {
		errors['hasNumeric'] = true;
	}

	if(!/[\^$*.[\]{}()?\"!@#%&/\\,><':;|_~`]/.test(value)) {
		errors['hasSpecial'] = true;
	}

  	return Object.keys(errors).length > 0 ? errors : null;
}

interface AssignedRoleFormValue {
    id: string;
    name: string;
}

@Component({
	selector: 'app-user-form',
	standalone: true,
	imports: [CommonModule, MatButtonModule, MatIconModule, MatDialogTitle, MatDialogContent, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSlideToggleModule, MatAutocompleteModule],
	templateUrl: './user-form.html',
	styleUrl: './user-form.css'
})

export class UserForm {
	userForm       : FormGroup;
	filteredRoles! : Observable<Role[]>;
  	isEditMode     : boolean = false;
	roles          : Role[] = [];

	hidePassword      = true;
	roleFilterControl = new FormControl<string | Role>('');

	constructor(private fb: FormBuilder, public dialogRef: MatDialogRef<UserForm, UserFormData | undefined>, @Inject(MAT_DIALOG_DATA) public data: UserFormData | null, private adminService: AdminService) {
		this.isEditMode = !!data;

		this.userForm = this.fb.group({
			email       : [data?.email || '', [Validators.required, Validators.email]],
			password    : ['', this.isEditMode ? [] : [Validators.required, passwordComplexityValidator]],
			displayName : [data?.displayName || '', [Validators.required]],
			disabled    : [this.isEditMode ? (this.data?.disabled ?? false) : true],
			roleIds     : this.fb.array<AssignedRoleFormValue>([], Validators.required)
		});

		this.roleFilterControl = new FormControl<string | Role>('', UserForm.roleObjectValidator);
	}

	ngOnInit(): void {
		this.loadRoles();

		this.filteredRoles = this.roleFilterControl.valueChanges.pipe(startWith(''), map(value => {
			const searchString = typeof value === 'string' ? value : (value as Role)?.name || '';

			return searchString ? this._filterRoles(searchString) : this.getAvailableRolesForSelection();
		}));
	}

	static roleObjectValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;

        if(value && typeof value === 'string' && value.trim() !== '') {
            return { notARoleObject: true };
        }

        return null;
    }

	private _filterRoles(value: string): Role[] {
		const filterValue = value.toLowerCase();

		return this.getAvailableRolesForSelection().filter(role => role.name.toLowerCase().includes(filterValue));
	}

	getAvailableRolesForSelection(): Role[] {
		const assignedRoleIds = new Set(this.assignedRolesControl.controls.map(g => (g as FormGroup).get('id')?.value));

		return this.roles.filter(role => !assignedRoleIds.has(role.id));
	}

	displayRoleName(role: Role | string): string {
        if(typeof role === 'object' && role !== null && 'name' in role) {
            return role.name;
        }

        return String(role || '');
    }

	async loadRoles(): Promise<void> {
		const result = await this.adminService.getAllRoles();

		if(result) {
			this.roles = result;
            
            if(this.isEditMode && this.data?.roleIds && this.data.roleIds.length > 0) {
                this.initializeRolesForm(this.data.roleIds);
            }
		}
	}

	initializeRolesForm(userRoleIds: string[]): void {
		this.assignedRolesControl.clear(); // Limpia cualquier valor preexistente

        userRoleIds.forEach(roleId => {
            const role = this.roles.find(r => r.id === roleId);

            if(role) {
                this.assignedRolesControl.push(this.fb.group({
                    id   : [role.id],
                    name : [role.name]
                }));
            }
        });

        this.assignedRolesControl.markAsTouched();
	}

	onAddRole(): void {
		if(this.roleFilterControl.invalid) {
			this.roleFilterControl.markAsTouched();

			return;
		}

		const role = this.roleFilterControl.value as Role;

		if(role && !this.assignedRolesControl.controls.some(g => (g as FormGroup).get('id')?.value === role.id)) {
			this.assignedRolesControl.push(this.fb.group({
				id   : [role.id],
				name : [role.name]
			}));

			this.roleFilterControl.setValue('');
			this.roleFilterControl.markAsUntouched();
			this.roleFilterControl.markAsPristine();
			this.assignedRolesControl.markAsDirty();
		}
	}

	onRemoveRole(index: number): void {
        this.assignedRolesControl.removeAt(index);
		this.assignedRolesControl.markAsDirty();
    }

	get emailControl() {
		return this.userForm.get('email');
	}

	get passwordControl() {
		return this.userForm.get('password');
	}

	get displayNameControl() {
		return this.userForm.get('displayName');
	}

	get assignedRolesControl(): FormArray {
        return this.userForm.get('roleIds') as FormArray;
    }

	togglePasswordVisibility(): void {
		this.hidePassword = !this.hidePassword;
	}

	onSubmit(): void {
		this.userForm.markAllAsTouched();

		if(this.userForm.valid) {
			const formData    = this.userForm.getRawValue();
			const finalRoleIds = formData.roleIds.map((role: AssignedRoleFormValue) => role.id);

            const userToSend: UserFormData = {
                email       : formData.email,
                password    : formData.password,
                displayName : formData.displayName,
                disabled    : formData.disabled,
                roleIds     : finalRoleIds
            };

            if(this.isEditMode && userToSend.password === '') {
                delete userToSend.password;
            }

            if(this.isEditMode && this.data?.uid) {
                userToSend.uid = this.data.uid;
            }

			this.dialogRef.close(userToSend);
		}
	}

	onCancel(): void {
		this.dialogRef.close(undefined);
	}
}
